<template>
    <div
        :class="styleClass"
        class="box box-chart">
        <div class="box-header with-border">
            <h3 class="box-title">{{ title }}</h3>

            <div class="box-tools">
                <form
                    class="input-group input-group-sm pull-left"
                    style="width: 150px; margin-right: 1rem"
                    @submit.prevent="search">
                    <input
                        v-model="moreParams.name"
                        type="text"
                        name="table_search"
                        class="form-control pull-right"
                        placeholder="Išči">

                    <div class="input-group-btn">
                        <button
                            type="submit"
                            class="btn btn-default">
                            <i class="fa fa-search"/>
                        </button>
                    </div>
                </form>
            </div>
        </div>
        <div
            :class="isLoading ? 'loading' : ''"
            class="box-body table-responsive no-padding">
            <div class="loader">
                <img
                    src="/images/spinner.svg"
                    alt="Nalagam"
                    width="200"
                    height="200">
                <b>NALAGAM</b>
            </div>
            <vuetable
                ref="vuetable"
                :api-url="api_route"
                :fields="fields"
                :css="css.table"
                :initial-page="initialPage"
                :per-page="perPage"
                :sort-order="sortOrder"
                :multi-sort="true"
                :append-params="moreParams"
                pagination-path="meta"
                @vuetable:loaded="dataLoaded"
                @vuetable:cell-clicked="onCellClicked"
                @vuetable:pagination-data="onPaginationData"/>
            <vuetable-total 
                ref="total"
                :css="css.table"
                :fields="fields"/>
        </div>
        <div class="box-footer clearfix">
            <div class="vuetable-pagination pagination pagination-sm no-margin pull-left">
                <p>Zadetkov na stran</p>
                <select
                    v-model="perPage"
                    @change="changePerPage">
                    <option
                        v-for="option in perPageOptions"
                        :key="option"
                        :value="option">
                        {{ option }} / stran
                    </option>
                </select>
            </div>
            <div class="vuetable-pagination pagination pagination-sm no-margin pull-right">
                <vuetable-pagination-info
                    ref="paginationInfo"
                    info-template="Prikazujem {from} do {to} od {total} zapisov"
                    info-class="pagination-info"/>
                <vuetable-pagination-dropdown
                    ref="pagination"
                    :css="css.dropdownPagination"
                    page-text="stran"
                    @vuetable-pagination:change-page="onChangePage"/>
            </div>
        </div>
    </div>
</template>

<script>
import Vuetable                   from 'vuetable-2/src/components/Vuetable';
import VuetablePagination         from 'vuetable-2/src/components/VuetablePagination';
import VuetablePaginationDropdown from '../VuetablePaginationDropdownWithFirstAndLast';
import VuetablePaginationInfo     from 'vuetable-2/src/components/VuetablePaginationInfo';
import VuetableTotal              from '../VuetableTotal';
import TablePagination            from '../../mixins/TablePagination';
import Table                      from '../../mixins/Table';
import TableUrlParams             from '../../mixins/TableUrlParams';
import ApiRoutes                  from '../../mixins/ApiRoutes';

export default {
    name: 'Combined',

    components: {
        Vuetable,
        VuetablePagination,
        VuetablePaginationDropdown,
        VuetablePaginationInfo,
        VuetableTotal
    },

    filters: {
        truncate(text, length, clamp) {
            text = text || '';
            clamp = clamp || '...';
            length = length || 30;

            if (text.length <= length) return text;

            let tcText = text.slice(0, length - clamp.length);
            let last = tcText.length - 1;


            while (last > 0 && tcText[last] !== ' ' && tcText[last] !== clamp[0]) last -= 1;

            // Fix for case when text dont have any `space`
            last = last || length - clamp.length;

            tcText = tcText.slice(0, last);

            return tcText + clamp;
        },

        humanReadableNumber(value) {
            value = !isNaN(value) ? Number(value) : 0;
            return value.toLocaleString('sl-SI', {style: 'decimal', useGrouping: true});
        }
    },

    mixins: [
        ApiRoutes,
        Table,
        TableUrlParams,
        TablePagination
    ],

    props: {
        title: {
            required: true,
            type:     String
        },

        styleClass: {
            type:    String,
            default: 'box-default'
        },
    },

    data() {
        return {
            fields:  [
                {
                    name:       '__sequence',
                    title:      '#',
                    titleClass: 'center aligned',
                    dataClass:  'right aligned'
                },
                {
                    name:      'name',
                    title:     'vir',
                    callback:  value => this.$options.filters.truncate(value, 150),
                    sortField: 'name'
                },
                {
                    name:      'phones',
                    title:     'št. stacionarnih telefonskih številk',
                    callback:  value => this.$options.filters.humanReadableNumber(value),
                    sortField: 'phones'
                },
                {
                    name:      'mobile_phones',
                    title:     'št. mobilnih telefonskih številk',
                    callback:  value => this.$options.filters.humanReadableNumber(value),
                    sortField: 'mobile_phones'
                },
                {
                    name:      'addresses',
                    title:     'št. naslovov',
                    callback:  value => this.$options.filters.humanReadableNumber(value),
                    sortField: 'addresses'
                },
                {
                    name:      'emails',
                    title:     'št. e-mailov',
                    callback:  value => this.$options.filters.humanReadableNumber(value),
                    sortField: 'emails',
                },
                {
                    name:      'total',
                    title:     'skupaj',
                    callback:  value => this.$options.filters.humanReadableNumber(value),
                    sortField: 'total',
                },
            ],
            perPage: 12,

            sortOrder: [{
                field:     'total',
                sortField: 'total',
                direction: 'desc'
            }],

            css: {
                table: {
                    tableClass: 'table table-striped table-hover'
                }
            },
        };
    },

    watch: {
        isLoading(current, previous) {
            if (!current) {
                this.$refs.total.equalizeWidths();
            }
        }
    },

    methods: {
        search() {
            this.dataLoading();

            Vue.nextTick(() => this.$refs.vuetable.refresh());
        },

        onChangePage(page) {
            this.$refs.vuetable.changePage(page);

            this.dataLoading();
        },

        onPaginationData(paginationData) {
            this.$refs.pagination.setPaginationData(paginationData);
            this.$refs.paginationInfo.setPaginationData(paginationData);
            this.$refs.total.setPaginationData(paginationData);

            if (this.paginationWithHistoryUrl) {
                this.historyUrl(paginationData);
            }
        },
    }
};
</script>


// WEBPACK FOOTER //
// resources/assets/js/components/Statistics/Combined.vue